/* @flow */

import type { NETGEM_API_V8_FEED, NetgemApiV8ItemLocation } from '../../../../libs/netgemLibrary/v8/types/FeedItem';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { NETGEM_API_VIEWINGHISTORY, NETGEM_API_VIEWINGHISTORY_ITEM, NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS, VIEWING_HISTORY_TYPE } from '../../../../libs/netgemLibrary/v8/types/ViewingHistory';
import type { CombinedReducers } from '../../../reducers';
import type { Dispatch } from '../../../types/types';
import type { RequestResponseMethodDefinitionType } from '../emitter';

import AccurateTimestamp from '../../../../helpers/dateTime/AccurateTimestamp';
import { MILLISECONDS_PER_WEEK, MIN_DATE } from '../../../../helpers/dateTime/Format';
import { logInfo, logWarning } from '../../../../helpers/debug/debug';
import { type AllSettledPromises, SettledPromiseRejected } from '../../../../helpers/jsHelpers/promise';
import { isUndefinedOrNull } from '../../../../helpers/jsHelpers/types';
import Localizer from '../../../../helpers/localization';
import { StorageKeys } from '../../../../helpers/localStorage/keys';
import LocalStorageManager from '../../../../helpers/localStorage/localStorageManager';
import { Messenger, MessengerEvents } from '../../../../helpers/messenger';
import ProfilesManager from '../../../../helpers/profiles/profilesManager';
import { arePlayedItemsDifferent, areViewingHistoryArraysDifferent } from '../../../../helpers/ui/section/comparisons';
import { CustomNetworkError } from '../../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { PersonalDataKey } from '../../../../libs/netgemLibrary/personalData/constants/keys';
import { HttpStatus } from '../../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { getAllLocationIds } from '../../../../libs/netgemLibrary/v8/helpers/Feed';
import { getLocationType } from '../../../../libs/netgemLibrary/v8/helpers/Item';
import { deleteViewingHistoryItem, deleteViewingHistoryItemETag, resetViewingHistory, setViewingHistoryError, updateViewingHistoryItem, updateWholeViewingHistory } from '../../../ui/actions';
import { sendPersonalDataListDeleteRequest } from './delete';
import { sendPersonalDataListGetRequest } from './get';
import { sendPersonalDataListPostRequest } from './post';

// Clean up viewing history once a week (in ms)
const VIEWING_HISTORY_CLEAN_UP_INTERVAL = MILLISECONDS_PER_WEEK;

const ONE_HUNDRED = 100;

const getViewingHistory: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        viewingHistoryETagCache: { viewingHistory: initialETag },
      },
    } = getState();

    const viewingHistoryKey = ProfilesManager.buildCurrentProfileKey(PersonalDataKey.ViewingHistory);

    return dispatch(sendPersonalDataListGetRequest(viewingHistoryKey, undefined, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag: viewingHistoryETag } = data;
        const result = JSON.parse(data.result as string);

        // Filter out malformed items
        const viewingHistory: NETGEM_API_VIEWINGHISTORY = validateViewingHistory(result as NETGEM_API_VIEWINGHISTORY);

        dispatch(updateWholeViewingHistory(viewingHistory, viewingHistoryETag));
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (status === HttpStatus.NotFound) {
          // Not found means viewing history does not exist yet
          dispatch(resetViewingHistory());
          return;
        } else if (status === HttpStatus.NotModified) {
          return;
        }

        dispatch(setViewingHistoryError());
        throw error;
      });
  };

const validateViewingHistory: (viewingHistory: NETGEM_API_VIEWINGHISTORY) => NETGEM_API_VIEWINGHISTORY = (viewingHistory) => {
  if (isUndefinedOrNull(viewingHistory)) {
    return {};
  }

  const validatedViewingHistory: NETGEM_API_VIEWINGHISTORY = {};

  Object.keys(viewingHistory).forEach((key) => {
    if (typeof key === 'string' && key !== '') {
      const viewingHistoryItem = viewingHistory[key];
      const { date, id, watchingstatus } = viewingHistoryItem;

      if (typeof date === 'string' && typeof id === 'string') {
        if (typeof watchingstatus !== 'number' || watchingstatus < 0 || watchingstatus > ONE_HUNDRED) {
          viewingHistoryItem.watchingstatus = 0;
        }
        validatedViewingHistory[key] = viewingHistoryItem;
      }
    }
  });

  return validatedViewingHistory;
};

const updateViewingHistory: (item: NETGEM_API_VIEWINGHISTORY_ITEM, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (item, signal, isSecondAttempt) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: { viewingHistory, viewingHistoryETagCache },
    } = getState();
    const { id: itemId } = item;
    const { [itemId]: existingItem } = viewingHistory || {};
    const { [itemId]: existingItemETag } = viewingHistoryETagCache;

    const viewingHistoryKey = ProfilesManager.buildCurrentProfileKey(PersonalDataKey.ViewingHistory);

    if (isSecondAttempt || (existingItem && !existingItemETag)) {
      // Item is already in viewing history, but ETag is missing (i.e. item was saved during a previous session): a GET is required to retrieve the associated ETag
      return dispatch(sendPersonalDataListGetRequest(viewingHistoryKey, itemId, existingItemETag, signal))
        .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
          const { eTag } = data;
          return dispatch(updateViewingHistoryWithETag(item, eTag, signal, isSecondAttempt));
        })
        .catch((error) => {
          // GET request failed: nothing to update
          Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('settings.content.viewing_history.update_error'));
          throw error;
        });
    }

    // Item and associated ETag exist (i.e. item saved during current session) or none of them exists (i.e. item never saved)
    return dispatch(updateViewingHistoryWithETag(item, existingItemETag, signal, isSecondAttempt));
  };

const updateViewingHistoryWithETag: (item: NETGEM_API_VIEWINGHISTORY_ITEM, eTag: ?string, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (item, eTag, signal, isSecondAttempt) =>
  (dispatch: Dispatch): Promise<void> => {
    const { id: itemId } = item;

    const viewingHistoryKey = ProfilesManager.buildCurrentProfileKey(PersonalDataKey.ViewingHistory);

    return dispatch(sendPersonalDataListPostRequest(viewingHistoryKey, itemId, item, eTag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag: itemETag } = data;

        if (itemETag) {
          dispatch(updateViewingHistoryItem(item, itemETag));
        }
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (!isSecondAttempt && (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed || status === HttpStatus.PreconditionRequired)) {
          /*
           * Most probable cause: another device paired with the same account already updated the viewing history
           * So we send a GET request to retrieve the latest ETag, but we only try once to avoid infinite loop between multiple devices
           */
          dispatch(deleteViewingHistoryItemETag(itemId));
          return dispatch(updateViewingHistory(item, signal, true));
        }

        throw error;
      });
  };

const deleteWholeViewingHistory: (profileId?: string, signal?: AbortSignal) => RequestResponseMethodDefinitionType = (profileId, signal) => deleteViewingHistory(profileId, null, signal);

const deleteViewingHistory: (profileId?: string, itemId: ?string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (profileId, itemId, signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        viewingHistoryETagCache,
        viewingHistoryETagCache: { viewingHistory: initialListETag },
      },
    } = getState();
    const initialItemETag = itemId ? viewingHistoryETagCache[itemId] : null;
    const initialETag = itemId ? initialItemETag : initialListETag;

    const viewingHistoryKey = ProfilesManager.buildProfileKey(PersonalDataKey.ViewingHistory, profileId);

    // Get ETag
    return dispatch(sendPersonalDataListGetRequest(viewingHistoryKey, itemId, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        // Delete
        const { eTag } = data;
        if (eTag) {
          return dispatch(deleteViewingHistoryWithETag(profileId, itemId, eTag, signal));
        }
      })
      .catch((eTagError: CustomNetworkError) => {
        const status = eTagError.getStatus();
        if (status === HttpStatus.NotFound) {
          // Viewing history not found for this user: nothing to delete
          return;
        } else if (status === HttpStatus.NotModified) {
          // Viewing history has not recently changed: current ETag should be used
          return dispatch(deleteViewingHistoryWithETag(profileId, itemId, initialETag, signal));
        }

        return Promise.reject(eTagError);
      });
  };

const deleteViewingHistoryWithETag: (profileId?: string, itemId: ?string, eTag: ?string, signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (profileId, itemId, eTag, signal) =>
  (dispatch: Dispatch): Promise<void> =>
    dispatch(sendPersonalDataListDeleteRequest(ProfilesManager.buildProfileKey(PersonalDataKey.ViewingHistory, profileId), itemId, eTag, signal)).then(() => {
      if (itemId) {
        // Delete item from list
        dispatch(deleteViewingHistoryItem(itemId));
        return;
      }

      // Clear whole list
      dispatch(resetViewingHistory());
    });

const checkPlayedItems: (item: NETGEM_API_VIEWINGHISTORY_ITEM, allLocationIds: Set<string>, allowedTypes: Set<NetgemApiV8ItemLocation>) => NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS | null = (
  item,
  allLocationIds,
  allowedTypes,
) => {
  const { playeditems } = item;

  if (!playeditems) {
    return null;
  }

  const updatedPlayedItems: NETGEM_API_VIEWINGHISTORY_PLAYED_ITEMS = {};
  let hasBeenUpdated = false;
  Object.keys(playeditems).forEach((locationId) => {
    const value = playeditems[locationId];
    const locationType = getLocationType(locationId);

    if (allLocationIds.has(locationId) || !locationType || !allowedTypes.has(locationType)) {
      updatedPlayedItems[locationId] = { ...value };
    } else {
      hasBeenUpdated = true;
    }
  });

  return hasBeenUpdated ? updatedPlayedItems : playeditems;
};

const cleanUpViewingHistory: (viewingHistory: VIEWING_HISTORY_TYPE, feed: NETGEM_API_V8_FEED, locationTypes: Set<NetgemApiV8ItemLocation>) => RequestResponseMethodDefinitionType =
  (viewingHistory, feed, locationTypes) =>
  (dispatch: Dispatch): Promise<void> => {
    const now = AccurateTimestamp.now();
    const lastViewingHistoryCleanUpTimestamp = LocalStorageManager.loadIsoDate(StorageKeys.LastViewingHistoryCleanUp, MIN_DATE).getTime();

    if (now < lastViewingHistoryCleanUpTimestamp + VIEWING_HISTORY_CLEAN_UP_INTERVAL) {
      // Last clean up is too recent
      return Promise.resolve();
    }

    LocalStorageManager.save(StorageKeys.LastViewingHistoryCleanUp, AccurateTimestamp.nowAsDate());

    const promises: Array<Promise<void>> = [];

    const allLocationIds = getAllLocationIds(feed);
    const updatedViewingHistory: VIEWING_HISTORY_TYPE = [];
    const obsoleteItemIds = new Set<string>();

    // Check items
    viewingHistory.forEach((item) => {
      const { episodes, id: itemId, playeditems } = item;

      if (episodes) {
        // Series
        const updatedEpisodes: VIEWING_HISTORY_TYPE = [];
        episodes.forEach((episode) => {
          const updatedPlayedItems = checkPlayedItems(episode, allLocationIds, locationTypes);
          if (updatedPlayedItems && Object.keys(updatedPlayedItems).length > 0) {
            updatedEpisodes.push({
              ...episode,
              playeditems: updatedPlayedItems,
            });
          }
        });

        if (updatedEpisodes.length === 0) {
          obsoleteItemIds.add(itemId);
        } else if (areViewingHistoryArraysDifferent(updatedEpisodes, episodes)) {
          updatedViewingHistory.push({
            ...item,
            episodes: updatedEpisodes,
          });
        }
      } else {
        // Program
        const updatedPlayedItems = checkPlayedItems(item, allLocationIds, locationTypes);
        if (updatedPlayedItems === null || Object.keys(updatedPlayedItems).length === 0) {
          obsoleteItemIds.add(itemId);
        } else if (arePlayedItemsDifferent(updatedPlayedItems, playeditems)) {
          updatedViewingHistory.push({
            ...item,
            playeditems: updatedPlayedItems,
          });
        }
      }
    });

    obsoleteItemIds.forEach((id) => promises.push(dispatch(deleteViewingHistory(id))));
    updatedViewingHistory.forEach((item: NETGEM_API_VIEWINGHISTORY_ITEM) => promises.push(dispatch(updateViewingHistory(item))));

    if (promises.length === 0) {
      logInfo(Localizer.localize('viewing_history.cleanup.nothing_to_do'));
      return Promise.resolve();
    }

    logInfo(Localizer.localize('viewing_history.cleanup.issues', { deleteCount: obsoleteItemIds.size, updateCount: updatedViewingHistory.length }));

    return Promise.allSettled(promises).then((results: AllSettledPromises<void>) => {
      const count = results.filter(({ status }) => status === SettledPromiseRejected).length;

      if (count > 0) {
        logWarning(Localizer.localize('viewing_history.cleanup.failure', { count }));
      } else {
        logInfo(Localizer.localize('viewing_history.cleanup.success', { count: obsoleteItemIds.size }));
      }
    });
  };

export { cleanUpViewingHistory, deleteViewingHistory, deleteWholeViewingHistory, getViewingHistory, updateViewingHistory };
